<script>
import Modal from 'dashboard/components/Modal.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';
import ConversationAPI from 'dashboard/api/conversations';
import messageAPI from 'dashboard/api/inbox/message';
import { useAlert } from 'dashboard/composables';

export default {
  name: 'ForwardMessageModal',
  components: {
    Modal,
    NextButton,
    ChannelIcon,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      show: true,
      conversations: [],
      conversationSearch: '',
      loading: false,
      forwarding: false,
      selectedConversationIds: new Set(),
    };
  },
  computed: {
    filteredConversations() {
      if (!this.conversationSearch) return this.conversations;
      return this.conversations.filter(conversation => {
        const sender = conversation.meta?.sender;
        const text = `${conversation.id} ${sender?.name || ''} ${
          sender?.email || ''
        }`.toLowerCase();
        return text.includes(this.conversationSearch.toLowerCase());
      });
    },
  },
  async mounted() {
    await this.$store.dispatch('inboxes/get');
    await this.fetchConversations();
  },
  methods: {
    inboxName(conversation) {
      const inboxId = conversation?.inbox_id;
      if (!inboxId) return null;
      const inbox = this.$store.getters['inboxes/getInbox'](inboxId);
      return inbox?.name || null;
    },
    conversationInboxForIcon(conversation) {
      const inboxId = conversation?.inbox_id;
      if (inboxId) {
        const inbox = this.$store.getters['inboxes/getInbox'](inboxId);
        if (inbox) return inbox;
      }

      if (conversation?.meta?.channel) {
        return { channel_type: conversation.meta.channel };
      }

      return null;
    },
    async fetchConversations() {
      this.loading = true;
      try {
        const response = await ConversationAPI.get();
        this.conversations = response.data?.data?.payload || [];
      } catch (error) {
        console.error('Error fetching conversations:', error);
        this.conversations = [];
      } finally {
        this.loading = false;
      }
    },
    handleClose() {
      this.show = false;
      this.onClose();
    },
    toggleConversation(conversationId) {
      if (this.selectedConversationIds.has(conversationId)) {
        this.selectedConversationIds.delete(conversationId);
      } else {
        this.selectedConversationIds.add(conversationId);
      }
    },
    isSelected(conversationId) {
      return this.selectedConversationIds.has(conversationId);
    },
    handleSelectAll() {
      if (
        this.filteredConversations.length > 0 &&
        this.selectedConversationIds.size === this.filteredConversations.length
      ) {
        this.selectedConversationIds.clear();
      } else {
        this.selectedConversationIds = new Set(
          this.filteredConversations.map(conv => conv.id)
        );
      }
    },
    async handleForward() {
      if (this.selectedConversationIds.size === 0) {
        return;
      }

      this.forwarding = true;
      try {
        const conversationId = this.message.conversation_id || this.message.conversationId;
        const messageId = this.message.id;
        const targetConversationIds = Array.from(this.selectedConversationIds);

        await messageAPI.forwardMessage(conversationId, messageId, targetConversationIds);
        useAlert(this.$t('CONVERSATION.FORWARD_MESSAGE.SUCCESS'));
        this.handleClose();
      } catch (error) {
        console.error('Error forwarding message:', error);
        const errorMessage =
          error?.response?.data?.error ||
          error?.message ||
          this.$t('CONVERSATION.FORWARD_MESSAGE.ERROR');
        useAlert(errorMessage);
      } finally {
        this.forwarding = false;
      }
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="handleClose">
    <div class="space-y-6 p-6">
      <!-- Header Section -->
      <div class="space-y-1">
        <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {{ $t('CONVERSATION.FORWARD_MESSAGE.DESCRIPTION') }}
        </h3>
        <p class="text-sm text-slate-500 dark:text-slate-400">
          {{ $t('CONVERSATION.FORWARD_MESSAGE.SELECT_CONVERSATION') }}
          ({{ selectedConversationIds.size }}/{{ conversations.length }})
        </p>
      </div>

      <!-- Conversations List Section -->
      <div class="space-y-3 rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-xs uppercase tracking-wide text-slate-500">
              {{ $t('CONVERSATION.FORWARD_MESSAGE.CONVERSATIONS_LABEL') }}
            </p>
            <h4 class="text-lg font-semibold">
              {{ $t('CONVERSATION.FORWARD_MESSAGE.SELECT_CONVERSATION') }}
            </h4>
          </div>
          <NextButton
            v-if="conversations.length > 0"
            variant="ghost"
            color="slate"
            size="sm"
            @click="handleSelectAll"
          >
            {{
              selectedConversationIds.size === filteredConversations.length &&
              filteredConversations.length > 0
                ? $t('KANBAN.BULK_ACTIONS.DESELECT_ALL')
                : $t('KANBAN.BULK_ACTIONS.SELECT_ALL')
            }}
          </NextButton>
        </div>

        <div v-if="!loading" class="relative">
          <span
            class="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-500"
          >
            <fluent-icon icon="search" size="16" />
          </span>
          <input
            v-model="conversationSearch"
            type="search"
            class="w-full pl-3 pr-10 py-2 border border-slate-200 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-woot-500 transition-colors"
            placeholder="Buscar por ID, nome ou email"
          />
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="flex justify-center items-center py-12">
          <span class="loading-spinner" />
        </div>

        <!-- Empty State -->
        <div
          v-else-if="filteredConversations.length === 0"
          class="rounded-xl border border-dashed border-slate-200 bg-slate-50 py-12 text-center"
        >
          <p class="text-sm text-slate-500">
            Nenhuma conversa encontrada
          </p>
        </div>

        <!-- Conversations List -->
        <div v-else class="max-h-72 overflow-y-auto space-y-1.5">
          <div
            v-for="conversation in filteredConversations"
            :key="conversation.id"
            :class="[
              'flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-2 text-sm shadow-sm transition',
              isSelected(conversation.id)
                ? 'border-woot-200 bg-woot-25 hover:border-woot-300'
                : 'border-slate-100 hover:border-woot-200 hover:bg-woot-25'
            ]"
            @click="toggleConversation(conversation.id)"
          >
            <input
              type="checkbox"
              :checked="isSelected(conversation.id)"
              class="mt-0.5 h-4 w-4 rounded border-slate-300 text-woot-600 focus:ring-woot-500"
              @click.stop
            />
            <div class="flex-1">
              <p class="font-medium text-slate-900 leading-5">
                {{ conversation.meta?.sender?.name || `Conversation #${conversation.id}` }}
              </p>
              <p
                v-if="inboxName(conversation)"
                class="text-xs text-slate-500 leading-4 flex items-center gap-1.5"
              >
                <ChannelIcon
                  v-if="conversationInboxForIcon(conversation)"
                  :inbox="conversationInboxForIcon(conversation)"
                  class="size-[14px] flex-shrink-0 text-slate-600 dark:text-slate-300"
                />
                <span class="truncate">{{ inboxName(conversation) }}</span>
              </p>
              <p
                v-else-if="conversation.meta?.channel"
                class="text-xs text-slate-500 leading-4 flex items-center gap-1.5"
              >
                <ChannelIcon
                  :inbox="{ channel_type: conversation.meta.channel }"
                  class="size-[14px] flex-shrink-0 text-slate-600 dark:text-slate-300"
                />
                <span class="truncate">{{ conversation.meta.channel }}</span>
              </p>
              <p v-else class="text-xs text-slate-500 leading-4">
                ID: {{ conversation.id }}
              </p>
            </div>
            <span
              v-if="isSelected(conversation.id)"
              class="inline-flex items-center rounded-full bg-woot-100 px-2.5 py-0.5 text-xs font-semibold text-woot-700"
            >
              {{ $t('CONVERSATION.FORWARD_MESSAGE.SELECTED') }}
            </span>
          </div>
        </div>
      </div>

      <!-- Summary Section -->
      <div class="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
        <p class="text-sm text-slate-600">
          {{
            $t('CONVERSATION.FORWARD_MESSAGE.SUMMARY', {
              count: selectedConversationIds.size,
              total: conversations.length,
            })
          }}
        </p>
      </div>

      <!-- Actions -->
      <div class="flex justify-end gap-2">
        <NextButton
          variant="ghost"
          color="slate"
          size="sm"
          @click="handleClose"
        >
          {{ $t('CONVERSATION.FORWARD_MESSAGE.CANCEL') }}
        </NextButton>
        <NextButton
          variant="solid"
          color="blue"
          size="sm"
          :disabled="selectedConversationIds.size === 0 || forwarding"
          :loading="forwarding"
          @click="handleForward"
        >
          {{ $t('CONVERSATION.FORWARD_MESSAGE.FORWARD') }}
        </NextButton>
      </div>
    </div>
  </Modal>
</template>

